import {formatOrderCar} from '../shared/order-car-display';
import {ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {finalize} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {getOrderSourceLabel} from '../services/order-source-labels';
import {environment} from '../../environments/environment';
import {DataService} from '../services/data.service';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {TableSortState} from '../table-sort';
import {
    Address,
    CompleteOrder,
    Customer,
    DriverCarSummary,
    DriverSummary,
    NewOrderRequest,
    Order,
    OrdersPageResponse,
    ProposedCar
} from '../models/api.models';

@Component({
    selector: 'app-drivers',
    templateUrl: './drivers.component.html',
    styleUrls: ['./drivers.component.css', '../orders/orders.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DriversComponent implements OnInit, OnDestroy {
    driverIdentifier = '';
    carIdentifier = '';
    externalCar = false;
    driver?: DriverSummary;
    car?: DriverCarSummary;
    orders: CompleteOrder[] = [];
    selectedOrder?: CompleteOrder;
    baseUrlForImages = environment.driverResourcesUrl;
    currentPage = 1;
    pageSize = 25;
    totalCount = 0;
    totalPages = 0;
    tableSort = new TableSortState();
    isLoadingDriver = false;
    isLoadingOrders = false;
    loadError = '';
    private sessionSubscription: Subscription;

    constructor(
        private activatedRoute: ActivatedRoute,
        private restService: RestService,
        private uiBlockerService: UIBlockerService,
        private dataService: DataService
    ) {
    }

    ngOnInit() {
        this.driverIdentifier = this.activatedRoute.snapshot.paramMap.get('driverIdentifier') || '';
        this.carIdentifier = this.activatedRoute.snapshot.queryParamMap.get('carIdentifier') || '';
        this.externalCar = this.activatedRoute.snapshot.queryParamMap.get('external') === 'true';
        this.loadDriver();
        this.sessionSubscription = this.dataService.dataChangedSession.subscribe(() => this.loadDriver());
    }

    ngOnDestroy() {
        if (this.sessionSubscription) {
            this.sessionSubscription.unsubscribe();
        }
    }

    refresh() {
        this.loadDriver();
    }

    loadDriver() {
        if (this.driverIdentifier === '' || isNullOrUndefined(this.dataService.getSessionIdentifier())
            || this.isLoadingDriver) {
            return;
        }

        this.isLoadingDriver = true;
        this.loadError = '';
        this.restService.getDriverHistory(this.driverIdentifier, this.carIdentifier, this.externalCar)
            .pipe(finalize(() => {
                this.isLoadingDriver = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe({
                next: (response) => {
                    if (!this.dataService.isResponseSuccessful(response) || isNullOrUndefined(response.Car)) {
                        this.setLoadError('The driver could not be loaded.');
                        return;
                    }
                    this.driver = response.Driver;
                    this.car = response.Car;
                    this.currentPage = 1;
                    this.loadOrders();
                },
                error: () => this.setLoadError('The driver could not be loaded.')
            });
    }

    loadOrders() {
        const carFilter = this.getCarFilter();
        if (carFilter === '' || isNullOrUndefined(this.dataService.getSessionIdentifier())) {
            return;
        }

        this.isLoadingOrders = true;
        this.loadError = '';
        this.restService.getOrders(
            this.currentPage, this.pageSize, '', carFilter, '', '', '', true, this.driverIdentifier)
            .pipe(finalize(() => {
                this.isLoadingOrders = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe({
                next: (response) => this.applyOrdersResponse(response),
                error: () => this.setLoadError('The orders for this car and driver could not be loaded.')
            });
    }

    changePageSize() {
        this.currentPage = 1;
        this.loadOrders();
    }

    goToPage(page: number) {
        if (page < 1 || page > this.totalPages || page === this.currentPage || this.isLoadingOrders) {
            return;
        }
        this.currentPage = page;
        this.loadOrders();
    }

    getFirstItemNumber() {
        return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    }

    getLastItemNumber() {
        return Math.min(this.currentPage * this.pageSize, this.totalCount);
    }

    getSortedOrders() {
        return this.tableSort.sort(this.orders, (order, column) => this.getOrderSortValue(order, column));
    }

    openOrderDetails(order: CompleteOrder) {
        this.selectedOrder = order;
    }

    closeOrderDetails() {
        this.selectedOrder = undefined;
    }

    getOrder(orderWrapper: CompleteOrder | undefined): Order | undefined {
        return isNullOrUndefined(orderWrapper) ? undefined : orderWrapper.Order;
    }

    getRequest(orderWrapper: CompleteOrder | undefined): NewOrderRequest | undefined {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.InitialOrderRequest;
    }

    getCustomer(orderWrapper: CompleteOrder | undefined): Customer | undefined {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.Customer;
    }

    getAssignedCar(orderWrapper: CompleteOrder | undefined): ProposedCar | undefined {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.AssignedCar;
    }

    getOrderStatus(orderWrapper: CompleteOrder | undefined) {
        const order = this.getOrder(orderWrapper);
        if (!isNullOrUndefined(orderWrapper) && this.hasValue(orderWrapper.CloseReason)) {
            return orderWrapper.CloseReason;
        }
        return this.valueOrDash(isNullOrUndefined(order) ? undefined : order.OrderState);
    }

    getOrderSource(orderWrapper: CompleteOrder | undefined) {
        const request = this.getRequest(orderWrapper);
        return this.getSourceLabel(isNullOrUndefined(request) ? undefined : request.DevicePlatform);
    }

    getSourceLabel(source: unknown) {
        return getOrderSourceLabel(source);
    }

    getClientName(orderWrapper: CompleteOrder | undefined) {
        const customer = this.getCustomer(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(customer) ? undefined : customer.Name);
    }

    getClientPhone(orderWrapper: CompleteOrder | undefined) {
        const customer = this.getCustomer(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(customer) ? undefined : customer.PhoneNumber);
    }

    getPickupAddress(orderWrapper: CompleteOrder | undefined): Address | undefined {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.PickupAddress;
    }

    getEntryDate(orderWrapper: CompleteOrder | undefined) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.EntryDate;
    }

    getCarDisplay(orderWrapper: CompleteOrder | undefined) {
        return formatOrderCar(this.getAssignedCar(orderWrapper));
    }

    getCurrentCarDisplay() {
        return formatOrderCar(this.car);
    }

    getPrice(orderWrapper: CompleteOrder | undefined) {
        const order = this.getOrder(orderWrapper);
        const value = Number(isNullOrUndefined(order) ? undefined : order.TaximeterTotalPrice);
        return isNaN(value) ? '-' : value.toFixed(2) + ' lei';
    }

    getPaymentMethod(orderWrapper: CompleteOrder | undefined) {
        return this.hasValue(isNullOrUndefined(orderWrapper) ? undefined : orderWrapper.PaymentMethodType)
            ? String(orderWrapper.PaymentMethodType).toLowerCase()
            : 'cash';
    }

    formatAddress(address: Address | undefined) {
        if (isNullOrUndefined(address)) {
            return '-';
        }
        if (this.hasValue(address.CompleteAddress)) {
            return address.CompleteAddress;
        }
        const parts = [];
        if (this.hasValue(address.StreetName)) {
            parts.push(address.StreetName);
        }
        if (this.hasValue(address.StreetNumber)) {
            parts.push('Nr. ' + address.StreetNumber);
        }
        if (this.hasValue(address.BlocNumber)) {
            parts.push('Bl. ' + address.BlocNumber);
        }
        if (this.hasValue(address.ScaraNumber)) {
            parts.push('Sc. ' + address.ScaraNumber);
        }
        if (parts.length === 0 && this.hasValue(address.Locality)) {
            parts.push(address.Locality);
        }
        return parts.length === 0 ? '-' : parts.join(', ');
    }

    private getCarFilter() {
        if (this.carIdentifier !== '') {
            return this.carIdentifier.trim();
        }
        if (isNullOrUndefined(this.car)) {
            return '';
        }
        return String(this.car.CarIdentifier || this.car.CarId || this.car.CarNumber || '').trim();
    }

    private getOrderSortValue(orderWrapper: CompleteOrder, column: string) {
        const order = this.getOrder(orderWrapper);
        switch (column) {
            case 'date':
                return this.getTimeValue(this.getEntryDate(orderWrapper));
            case 'client':
                return this.getClientName(orderWrapper);
            case 'source':
                return this.getOrderSource(orderWrapper);
            case 'phone':
                return this.getClientPhone(orderWrapper);
            case 'pickup':
                return this.formatAddress(this.getPickupAddress(orderWrapper));
            case 'car':
                return this.getCarDisplay(orderWrapper);
            case 'status':
                return this.getOrderStatus(orderWrapper);
            case 'price': {
                const price = Number(isNullOrUndefined(order) ? undefined : order.TaximeterTotalPrice);
                return isNaN(price) ? undefined : price;
            }
            case 'paymentMethod':
                return this.getPaymentMethod(orderWrapper);
            default:
                return '';
        }
    }

    private getTimeValue(value: string | Date | undefined) {
        if (isNullOrUndefined(value) || value === '') {
            return undefined;
        }
        const time = new Date(value).getTime();
        return isNaN(time) ? undefined : time;
    }

    private applyOrdersResponse(response: OrdersPageResponse) {
        const parsedResponse = this.parseResponse(response);
        this.orders = !isNullOrUndefined(parsedResponse) && Array.isArray(parsedResponse.Items)
            ? parsedResponse.Items : [];
        this.totalCount = Number(parsedResponse && parsedResponse.TotalCount) || 0;
        this.totalPages = Number(parsedResponse && parsedResponse.TotalPages) || 0;
        if (this.totalPages > 0 && this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages;
            this.loadOrders();
        }
    }

    private parseResponse(response: OrdersPageResponse | string): OrdersPageResponse {
        if (typeof response !== 'string') {
            return response;
        }
        try {
            return JSON.parse(response) as OrdersPageResponse;
        } catch {
            return {StatusCode: -1};
        }
    }

    private setLoadError(message: string) {
        this.orders = [];
        this.totalCount = 0;
        this.totalPages = 0;
        this.loadError = message;
    }

    private hasValue(value: unknown) {
        return !isNullOrUndefined(value) && String(value).trim() !== '';
    }

    private valueOrDash(value: unknown): string {
        return this.hasValue(value) ? String(value) : '-';
    }
}
